import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FileType } from "src/interfaces/KnowledgeBase";

// We keep a separate interface for the serializable state
interface AutoUploadState {
  fileName: string | null;
  type: FileType | null;
  level: string;
  subject: { id: number | null; name: string } | null;
  hasUploadedFile: boolean;
  currentStep: number;
  isLoading: boolean;
  fileBeingParsed: {
    isModalBeingForcedClosed: boolean;
    fileName: string | null;
    isBeingParsed: boolean;
    praseSuccess: boolean;
    praseError: boolean;
    parseSuccessResponse: JSON | null;
  };
  studentDataUpdating: {
    isUpdating: boolean;
    info: string | null;
    processedCount: number;
    fileId: string | null;
  };
}

// Define the initial state for the slice
const initialState: AutoUploadState = {
  fileName: null,
  type: null,
  level: "",
  subject: null,
  hasUploadedFile: false,
  currentStep: 1,
  isLoading: false,
  fileBeingParsed: {
    isModalBeingForcedClosed: false,
    fileName: null,
    isBeingParsed: false,
    praseSuccess: false,
    praseError: false,
    parseSuccessResponse: null,
  },
  studentDataUpdating: {
    isUpdating: false,
    info: null,
    processedCount: 0,
    fileId: null,
  },
};

const autoUploadSlice = createSlice({
  name: "autoUpload",
  initialState,
  reducers: {
    // Action to update the main form fields
    setFormFields: (
      state,
      action: PayloadAction<{
        fileName: string | null;
        type: FileType | null;
        level: string;
        subject: { id: number | null; name: string } | null;
      }>
    ) => {
      state.fileName = action.payload.fileName;
      state.type = action.payload.type;
      state.level = action.payload.level;
      state.subject = action.payload.subject;
      if (action.payload.fileName) {
        state.hasUploadedFile = true;
      }
    },
    // Action to set the current step of the form
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    // Action to toggle the loading state
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    resetFileName: (state) => {
      state.fileName = null;
    },
    // Action to initiate the parsing process
    startParsing: (state) => {
      state.fileBeingParsed.isBeingParsed = true;
      state.fileBeingParsed.fileName = state.fileName;
      state.fileBeingParsed.praseSuccess = false;
      state.fileBeingParsed.praseError = false;
      state.fileBeingParsed.parseSuccessResponse = null;
      state.fileBeingParsed.isModalBeingForcedClosed = false;
    },
    // Action to handle a successful parse
    parseSuccess: (state, action: PayloadAction<JSON>) => {
      state.fileBeingParsed.isBeingParsed = false;
      state.fileBeingParsed.praseSuccess = true;
      state.fileBeingParsed.parseSuccessResponse = action.payload;
      state.fileBeingParsed.isModalBeingForcedClosed = true;
    },
    // Action to handle a parsing error
    parseError: (state) => {
      state.fileBeingParsed.isBeingParsed = false;
      state.fileBeingParsed.praseError = true;
      state.fileBeingParsed.fileName = null;
      state.fileBeingParsed.isModalBeingForcedClosed = true;
    },

    setStudentDataUpdating: (state, action: PayloadAction<{
      isUpdating: boolean;
      info: string | null;
      processedCount: number;
      fileId: string | null;
    }>) => {
      state.studentDataUpdating = action.payload;
    },

    forceModalClose: (state) => {
      state.fileBeingParsed.isModalBeingForcedClosed = true;
    },
    // Action to reset the entire form state
    resetForm: (state) => {
      state.fileName = null;
      state.type = null;
      state.level = "";
      state.subject = null;
      state.hasUploadedFile = false;
      state.currentStep = 1;
      state.isLoading = false;
      state.fileBeingParsed.isBeingParsed = false;
      state.fileBeingParsed.praseSuccess = false;
      state.fileBeingParsed.praseError = false;
      state.fileBeingParsed.parseSuccessResponse = null;
      state.fileBeingParsed.isModalBeingForcedClosed = false;
    },
  },
});

export const {
  setFormFields,
  setCurrentStep,
  setIsLoading,
  startParsing,
  parseSuccess,
  parseError,
  resetForm,
  resetFileName,
  forceModalClose,
  setStudentDataUpdating,
} = autoUploadSlice.actions;

export default autoUploadSlice.reducer;
